import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';

interface IgnoredTokensContextType {
  ignoredAddresses: Set<string>;
  isIgnored: (address: string) => boolean;
  refresh: () => void;
  isLoading: boolean;
}

const IgnoredTokensContext = createContext<IgnoredTokensContextType>({
  ignoredAddresses: new Set(),
  isIgnored: () => false,
  refresh: () => {},
  isLoading: false,
});

export const useIgnoredTokens = () => useContext(IgnoredTokensContext);

export const IgnoredTokensProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [ ignoredAddresses, setIgnoredAddresses ] = useState<Set<string>>(new Set());
  const [ isLoading, setIsLoading ] = useState(false);

  const fetchIgnoredTokens = useCallback(async() => {
    if (typeof window === 'undefined') return;

    const token = localStorage.getItem('authToken');
    if (!token) {
      setIgnoredAddresses(new Set());
      return;
    }

    setIsLoading(true);
    try {
      const res = await fetch('/api/auth/token-ignore-list/addresses', {
        headers: { Authorization: `Bearer ${ token }` },
      });

      if (!res.ok) {
        setIgnoredAddresses(new Set());
        return;
      }

      const data = await res.json();
      const addrs: Array<string> = data.addresses || [];
      setIgnoredAddresses(new Set(addrs.map((a: string) => a.toLowerCase())));
    } catch {
      setIgnoredAddresses(new Set());
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchIgnoredTokens();

    // Re-check when storage changes (login/logout)
    const handleStorageChange = (e: StorageEvent) => {
      if (e.key === 'authToken') {
        fetchIgnoredTokens();
      }
    };
    window.addEventListener('storage', handleStorageChange);

    // Listen for custom event when ignore list is modified (add/remove/edit)
    const handleIgnoreListChange = () => {
      fetchIgnoredTokens();
    };
    window.addEventListener('ignoredTokensChanged', handleIgnoreListChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
      window.removeEventListener('ignoredTokensChanged', handleIgnoreListChange);
    };
  }, [ fetchIgnoredTokens ]);

  const isIgnored = useCallback((address: string) => {
    if (!address) return false;
    return ignoredAddresses.has(address.toLowerCase());
  }, [ ignoredAddresses ]);

  const value = useMemo(() => ({
    ignoredAddresses,
    isIgnored,
    refresh: fetchIgnoredTokens,
    isLoading,
  }), [ ignoredAddresses, isIgnored, fetchIgnoredTokens, isLoading ]);

  return (
    <IgnoredTokensContext.Provider value={ value }>
      { children }
    </IgnoredTokensContext.Provider>
  );
};

export default IgnoredTokensContext;

